<template>
<div class="chapter">
<div class="inner_page">
<div id="Chapter-One.html" xml:lang="en-US">
<div class="Basic-Text-Frame-26">
<p class="ch"><a id="Anchor-1"></a>ONE</p>
<p class="body-text-opening"><span class="lic" style="font-size:1.76em;" xml:lang="en-US">E</span>lizabeth Conners sat behind her desk at
					the Family Psychology Clinic. The office was comfortably furnished, with an oak
					desk and chair, a couple of four-drawer oak file cabinets, two oak side chairs
					and a sofa upholstered in dark green fabric sitting against one wall.</p>
<p class="body-text">Oak-framed pictures of the town in the early nineteen hundreds
					decorated the interior and a green-glass lamp sat on the edge of her desk,
					giving the place a casual, old-fashioned appearance. The office was neat and
					orderly. With the number of cases she handled, it was imperative she be well
					organized.</p>
<p class="body-text">Elizabeth glanced at the stack of manila files on her desk,
					each one a case she was currently working. For the past two years, she had been
					an employee of the small, privately owned clinic in San Pico, California.
					Elizabeth had been born in the town, mainly an agricultural community, situated
					near the southwest end of the San Joaquin Valley.</p>
<p class="body-text">She had graduated from San Pico High, then gotten a partial
					scholarship to help pay her way through college. She had majored in psychology
					at UCLA, earning a master’s in social work, making extra money with a part-time
					job as a waitress, as she had done in high school.</p>
<p class="body-text">Two years ago, she had returned to her hometown, a quiet place
					of refuge where her father and sister lived, though her dad had died last year
					and her sister had married and moved away. Elizabeth had come to recover from a
					messy divorce, and the quiet life away from the city had helped bring her out of
					the depression she had suffered after her marriage to Brian Logan had fallen
					apart.</p>
<p class="body-text">In contrast to the hustle of busy Santa Ana, where she had been
					working, San Pico was a city of around thirty thousand people, of which half the
					population was now Hispanic. Elizabeth’s family had been among the original
					founders back in 1907, farmers and dairymen back then. During her childhood, her
					father and mother had owned a small neighborhood market, Conners’ Grocery, but
					after her mother had died, her father had sold the business and retired, and
					Elizabeth had gone off to school.</p>
<p class="body-text">She reached for the file on top of the stack on her desk,
					preparing herself for her upcoming session that evening with the Mendoza family,
					conducted in their home. The file contained a history of drinking and family
					violence that included an incident of child abuse, but the violence seemed to
					have lessened in the months the family had been in counseling.</p>
<p class="body-text">Elizabeth fervently believed the sessions were helping family
					members learn to deal with each other in ways that did not include physical
					violence.</p>
<p class="body-text">Leaning over the file, she tucked an annoying strand of dark
					auburn hair behind an ear and continued to scan the file. Like all of the
					Conners, she was dark-haired, slenderly built and a little taller than average.
					But unlike her sister, she had been blessed with the clear blue eyes of her
					mother.</p>
<p class="body-text">Which meant that every time she looked in the mirror, she
					thought of Grace Conners and missed her.</p>
<p class="body-text">Her mother had died a painful death from cancer when Elizabeth
					was just fifteen. They had been extremely close and the difficult months of
					caring for, then losing her had taken its toll. Elizabeth’s blue eyes were her
					mother’s legacy, but the memories they stirred were so painful that sometimes,
					instead of a blessing, her best feature seemed more of a curse.</p>
<p class="body-text">Elizabeth sighed as she reached the end of the report, closed
					the file and leaned back in her chair. She had never expected to return to her
					hometown, which was flat and dusty and most of the year far too hot.</p>
<p class="body-text">But sometimes fate had different notions and here she was, in a
					rented apartment on Cherry Street, doing the kind of work she had been trained
					for, and though she didn’t particularly like living in the homely little town,
					at least she felt good about her job.</p>
<p class="body-text">She was thinking about her upcoming session that night when a
					soft knock sounded at the door. She looked up to see one of the boys she
					counseled walk into the room. Raul Perez was seventeen years old, on work leave
					from juvenile detention, to which he’d been sentenced for the second time.
					Belligerent, surly and difficult, he was also smart and caring and loyal to his
					friends and the people he loved, and especially to his beloved sister,
					Maria.</p>
<p class="body-text">His concern for others was the reason Elizabeth had agreed to
					do his counseling sessions without a fee. Raul had potential. He could make
					something of himself if he was given the right motivation—if she could convince
					him that his life would never improve as long as he involved himself in alcohol
					and drugs.</p>
<p class="body-text">Burglary had been the result, of course, as it often was with
					kids like Raul. They needed money to buy the drugs and they would do whatever it
					took to get them.</p>
<p class="body-text">But Raul had been drug free for over a year and he had told her
					that he meant to stay that way. There was something in his intense black eyes
					that made Elizabeth believe it might be true.</p>
<p class="body-text">“Raul. Come in.” She smiled at him warmly. “It’s good to see
					you.”</p>
<p class="body-text">“You are looking very well,” he said, always extremely
					polite.</p>
<p class="body-text">“Thank you.” She thought that she did look good today, in crisp
					beige cotton slacks and a short-sleeved turquoise silk blouse, her
					shoulder-length auburn hair in loose waves around her face.</p>
<p class="body-text">Raul sat down in one of the oak side chairs and Elizabeth sat
					down behind her desk. To begin the session, she started with a question about
					his part-time job at Sam Goodie’s, janitorial and delivery work that would end
					when Ritchie Jenkins got back on his feet after crashing his motorcycle down at
					the end of Main Street. In another week, the job would be over and unless he
					found something else, he would be back in juvenile detention full-time.</p>
<p class="body-text">“So, how do you like working at the store so far?”</p>
<p class="body-text">He shrugged a pair of linebacker shoulders. “I like the
					music—except when they play country western.” Raul was only about five foot ten,
					but he was stocky and muscular, big for his age since he was a child. He had
					glossy, straight black hair and dark skin, marred only by the tattoo of a skull
					on the back of one hand and his initials in blue under the skin beneath his left
					ear. The initials were a homemade job probably done in grammar school. She
					thought the skull must have been done during his last stay in juvenile hall.</p>
<p class="body-text">Elizabeth looked at him and smiled. “I know you’ll be out of
					work by the end of the week, but I have some very exciting news for you.”</p>
<p class="body-text">He studied her warily from his place across the desk. “What is
					it?”</p>
<p class="body-text">“You’ve been accepted at Teen Vision.”</p>
<p class="body-text">“Teen Vision?”</p>
<p class="body-text">“I mentioned it a couple of weeks ago, remember?”</p>
<p class="body-text">He nodded, his eyes fixed on her face.</p>
<p class="body-text">“Since the farm is a fairly new facility, they only have room
					for twenty-five kids so far, but a couple of openings have come up and your
					application was one of the ones they accepted.”</p>
<p class="body-text">“I didn’t put in an application,” he said darkly.</p>
<p class="body-text">She kept the smile fixed on her face. “I know you didn’t—I did.
					I mentioned the farm to you when you were here before. You seemed interested. I
					took it one step further and applied in your name.”</p>
<p class="body-text">He was frowning. That wasn’t good. The students who
					participated in the residential program at Teen Vision were there of their own
					free will. If he didn’t want to be involved, being there wouldn’t do him the
					least bit of good.</p>
<p class="body-text">“The term lasts one year. You have to be between the ages of
					fourteen and eighteen and you have to agree to stay the entire twelve months or
					they won’t let you in.”</p>
<p class="body-text">“I’m out of detention for good in six more months.”</p>
<p class="body-text">“You need to change your life so that you can stay out.”</p>
<p class="body-text">Raul said nothing.</p>
<p class="body-text">“You would start next week. While you’re there, your room and
					board would be completely taken care of. They even pay a small stipend for the
					work you do on the farm.”</p>
<p class="body-text">Raul grunted. “I know how much farm workers make. That’s the
					way my family earned their living.”</p>
<p class="body-text">“This is different than being a migratory worker, Raul. You
					told me yourself you liked farming, being out in the fresh air working the soil.
					You could learn a vocation while you’re there and you could get your GED. When
					your year is up, you could find a full-time job in agriculture or whatever you
					decide you want to do, something that would eventually make you a decent
					living.”</p>
<p class="body-text">He seemed to mull that over. “I need to think about it.”</p>
<p class="body-text">“All right. But I don’t think you can make any sort of decision
					until you go out there, take a look at the facility and meet some of the
					instructors. Would you be willing to do that, Raul?”</p>
<p class="body-text">He sat back in his chair, his eyes still on her face. “I would
					like to see it.”</p>
<p class="body-text">“That’s great. Just remember, a place like that requires a
					commitment. It’s a place you go in order to change your life. You have to want
					to do that. You have to want to start over and make a new beginning.”</p>
<p class="body-text">Raul said nothing for several long moments and neither did
					Elizabeth, purposely giving him time to think.</p>
<p class="body-text">“When could we go?”</p>
<p class="body-text">She stood up from her chair. “Do you have to work this
					afternoon?”</p>
<p class="body-text">He shook his head. “Not until tomorrow morning.”</p>
<p class="body-text">“Good.” Elizabeth rounded the desk and moved past him toward
					the door. She smiled and pulled it open. “Then why don’t we go right now?”</p>
<p class="tb">* * *</p>
<p class="body-text-no-tab">The Teen Vision farm sat on fifteen acres of flat, arid
					land fronting Highway 51 a few miles out of town. It was a fertile piece of
					ground donated by Harcourt Farms, the largest agricultural farming company in
					San Pico County.</p>
<p class="body-text">Until four years ago, Fletcher Harcourt had run the farm. After
					a nearly fatal accident that damaged the family patriarch’s brain and left him
					in a wheelchair, his oldest son, Carson, had taken over the twelve-thousand acre
					operation. He had taken control of the company and assumed his father’s
					once-powerful position in the community. Carson was well liked and generous. The
					attractive white stucco dormitory and outbuildings that housed Teen Vision had
					undoubtedly been funded in part by Carson’s donations.</p>
<p class="body-text">Elizabeth had met Carson Harcourt several times since her
					return to San Pico. He was tall, blond and attractive. At thirty-six, after
					several brief relationships, he remained unmarried, though with his considerable
					wealth and social position, he could certainly take his pick of the women in
					town.</p>
<p class="body-text">She was thinking of Carson as she drove her nearly new,
					pearl-white Acura through the front gates of Teen Vision and was only mildly
					surprised to see the man’s silver Mercedes sedan pulling out of the parking
					area. He stepped on the brake as he drove past her, bringing the car to a halt,
					swirling a cloud of dust around them. Carson rolled down his window as if he
					didn’t notice and gave her the famous Harcourt smile.</p>
<p class="body-text">“Well, Ms. Conners—what a nice surprise. Looks like I’m leaving
					at just the wrong time.” Carson had always been friendly. She had sensed he
					might have an interest beyond just being social, but if he did, he had never
					pursued it.</p>
<p class="body-text">“It’s nice to see you, Carson.” She tilted her head toward her
					passenger. “This is Raul Perez. I’m hoping he’ll be one of the farm’s new
					enrollees.”</p>
<p class="body-text">“Is that so?” Carson ducked his head to get a look at the boy.
					“They do some good work here, son. You had better grab the chance while you’ve
					got it.”</p>
<p class="body-text">Raul said nothing, as Elizabeth could have guessed. With the
					money and power Carson Harcourt possessed, he represented everything the boy
					rebelled against.</p>
<p class="body-text">“This place…” She glanced around, taking in the group of boys
					hoeing the fields, the two boys laughing as they poured grain into a trough to
					feed the farm’s small herd of four white-faced Hereford cattle. “This was very
					generous of you, Carson.”</p>
<p class="body-text">He shrugged. “Harcourt Farms likes to give back to the
					community whenever it can.”</p>
<p class="body-text">“Still, you’ve really done something good here. Someone else
					might not have been so supportive.”</p>
<p class="body-text">He smiled and glanced out at the fields, then back to her
					again. “Listen, I’ve got to run. Got a meeting with some labor union guys in
					town.” He ducked his head to look past her to the boy. “Good luck to you,
					son.”</p>
<p class="body-text">Raul just stared and inwardly Elizabeth sighed.</p>
<p class="body-text">“One more thing,” Carson said to her. “I’ve been meaning to
					call you. I wanted to talk to you about the Teen Vision Benefit on Saturday
					night. I was hoping you might go with me.”</p>
<p class="body-text">She was stunned. Carson had been friendly, but nothing more.
					Perhaps he had discovered her interest in Teen Vision. Though she had never
					actually been to the farm, she knew the wonderful work being done and believed
					strongly in the project.</p>
<p class="body-text">She cast him an assessing glance. Since her divorce, she’d
					rarely dated. The dark days after she had discovered Brian’s infidelity left her
					wary of men. Still, it might be fun to spend an evening with an intelligent,
					attractive man.</p>
<p class="body-text">“I’d like that, Carson. Thank you for asking. It’s black tie,
					as I recall.”</p>
<p class="body-text">He nodded. “I’ll call you at your office, get directions to
					your house so I can pick you up.”</p>
<p class="body-text">“All right, that sounds good.”</p>
<p class="body-text">He smiled and waved, rolled up the window of his Mercedes and
					drove away. Elizabeth watched him a moment in the rearview mirror, then stepped
					on the accelerator and drove through the gate into one of the spaces in the dirt
					parking lot and turned off the engine.</p>
<p class="body-text">“Well, we’re here.” She smiled at Raul, who was staring out the
					window toward the group of young men working in the fields. A distant tractor
					threw up a plume of dust while a cluster of dairy cows stood on a hill waiting
					for the evening round of milking to begin.</p>
<p class="body-text">Looking nervous and younger than his seventeen years, Raul
					cracked the door on his side of the car and climbed out into the afternoon heat.
					In the area between the parking lot and the house, the director of Teen Vision,
					Sam Marston, walked toward them.</p>
<p class="body-text">Sam was average in height and build, a man in his early forties
					rapidly going bald who had shaved the sparse hair off, giving him a modern,
					stylish appearance. He was a soft-spoken man, yet there was a sense of authority
					about him. He waved a greeting as he walked up to where they stood.</p>
<p class="body-text">“Welcome to Teen Vision.”</p>
<p class="body-text">“Thank you.” She had met Sam Marston when she first moved back
					to town, knew his remarkable work with delinquent boys. “I know your time is
					limited. I thought I could come back for an official tour later on.”</p>
<p class="body-text">He understood what she was saying. That she wanted him to spend
					this time with Raul. “You’re welcome anytime,” he said with a smile, then his
					attention shifted to the boy. “You must be Raul Perez.”</p>
<p class="body-text">“Yes, sir.”</p>
<p class="body-text">“I’m Sam Marston. Let me show you around, and while we’re at
					it, I’ll tell you a little about Teen Vision.” Ignoring Raul’s look of alarm,
					Sam slapped a hand on the youth’s wide back and nudged him forward, forcing Raul
					into step beside him.</p>
<p class="body-text">Elizabeth watched them walk away and found herself smiling. She
					prayed Raul would give the place a chance, that the farm would be his salvation,
					as it had been for a number of other boys.</p>
<p class="body-text">Walking over to stand in the shade of a fruit tree to watch the
					boys in the fields and wait for Sam, she saw another car, a dark brown Jeep
					Cherokee, drive through the gate and pull into the space next to hers.</p>
<p class="body-text">A tall, lean man in faded jeans and a navy blue T-shirt climbed
					out from behind the wheel. He had very dark hair and darkly tanned skin, a nice
					wide set of shoulders, narrow hips and a flat stomach. As he walked toward her,
					she saw that the shirt carried the Teen Vision slogan, Only You Can Make Your
					Dreams Come True, printed in white letters on the front. A pair of solid-looking
					biceps bulged below the short sleeves of his T-shirt.</p>
<p class="body-text">Still, somehow she couldn’t imagine him working as a counselor
					on the farm. His haircut looked too expensive, his long strides too purposeful,
					almost aggressive. Even the fit of his jeans spoke of style and money. Elizabeth
					studied him from beneath the tree and though he wore wraparound shades and she
					couldn’t make out his face, there was something familiar about him.</p>
<p class="body-text">She wondered where she might have seen him and thought that if
					she had, surely she would remember. He moved past her as if she weren’t there,
					his gaze focused ahead, striding with purpose in the direction of the new barn
					under construction where several older boys were busily hammering nails. The
					dark-haired man walked up to them and started talking. A few minutes later, he
					strapped on a carpenter’s belt and set to work.</p>
<p class="body-text">Elizabeth watched him for a while, enjoying the efficiency of
					his movements, his obvious skill at what he was doing, and continued to wonder
					who he was. When Sam and Raul returned, she intended to ask, but when they
					arrived, the boy’s face was glowing and his smile so radiant the moment slipped
					past.</p>
<p class="body-text">“You’re going to do it?” she said, beaming up at him.</p>
<p class="body-text">He nodded. “Sam says he and one of the counselors will help me
					figure out what I am most suited to learn. He says I can do whatever I am most
					interested in.”</p>
<p class="body-text">“Oh, Raul, that’s wonderful!” She wanted to reach over and hug
					him, but she needed to remain professional and that would probably just
					embarrass him. “I can’t tell you how pleased I am.”</p>
<p class="body-text">“He can check in on Saturday,” Sam said. “We’ll help him fill
					out the forms and sign whatever paperwork is necessary.” Technically he would
					still be in the foster care system until next year and the paperwork would have
					to pass through proper channels.</p>
<p class="body-text">“That sounds great.” Elizabeth turned to Raul. “I can bring you
					out here, if you like.”</p>
<p class="body-text">“<em class="italic">Sí,</em> that would be good.” Raul
					rarely slipped into his native language, only when he was angry or nervous.
					Still, he was smiling. Sometimes nervous could be good.</p>
<p class="body-text">“Your sister will be so pleased.”</p>
<p class="body-text">His smile broadened. “Maria will be happy for me. Miguel, I
					think, too.”</p>
<p class="body-text">“Yes, I think they will both be very happy you made this
					decision.”</p>
<p class="body-text">They said their farewells to Sam, who promised to give her a
					personal tour of the farm whenever she had time, and they started back to the
					car.</p>
<p class="body-text">She was feeling extremely pleased with the way the afternoon
					had gone when she glanced at Raul and saw that his smile had faded.</p>
<p class="body-text">“What is it, Raul?”</p>
<p class="body-text">“I am nervous. I want to do this right.”</p>
<p class="body-text">“You will. You’ve got lots of people to help you.”</p>
<p class="body-text">Still, he didn’t relax. She knew he was worried that he would
					somehow fail. It was the failures, she had learned, that most of these young
					Hispanics remembered and those failures shaped their lives. But Raul had a
					number of accomplishments as well. He had stayed drug-free for a year and now he
					had pledged a year of his life to Teen Vision.</p>
<p class="body-text">“Will you be seeing your sister tonight? I know how excited
					she’ll be.”</p>
<p class="body-text">Instead of a smile, Raul frowned. “I will stop by and tell her
					the news.” He glanced in her direction. “I am worried about her.”</p>
<p class="body-text">“Why? She isn’t having trouble with her pregnancy, I hope?”
					Though Maria was just nineteen, this was her second pregnancy. Last year, she
					had suffered a miscarriage. Elizabeth knew how much this baby meant to her and
					Miguel.</p>
<p class="body-text">“It isn’t the baby. It is something else. Maria won’t say
					what.” His black eyes came to rest on her face. “Maybe you could talk to her. If
					you did, maybe she would tell you what is wrong.”</p>
<p class="body-text">She didn’t like the sound of that. Though Maria’s husband was a
					stereotypical macho Hispanic, convinced the man was the undisputed head of the
					family, the couple seemed happy. She hoped they weren’t having marital
					problems.</p>
<p class="body-text">“I’d be glad to talk to her, Raul. Tell her to call me at the
					office and we’ll set up a time.”</p>
<p class="body-text">“I will tell her. But I do not think she will call.” Raul said
					no more.</p>
<p class="body-text">As Elizabeth slid behind the wheel of the car, hissing at the
					heat of the red leather seat against her skin, she cast a last glance at the
					barn under construction. Only two sides of the building had been framed, but
					they were making good progress. She studied the group still hammering away, but
					the dark-haired man was gone.</p>
<p class="body-text">Sitting in the passenger side, Raul snapped his seat belt in
					place and Elizabeth started the engine. As they drove back to town, the boy
					seemed miles away and she wondered if his thoughts were on the very different
					future he was about to undertake, or if he was worried about his sister.</p>
<p class="body-text">Elizabeth made a mental note to stop by the little yellow house
					occupied by Miguel Santiago and his pretty young wife. She would speak to Maria,
					see what was wrong, find out if there was something she could do.</p>
</div>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Chapter_One.vue?191ec360